
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Clock, Edit, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ReviewReply } from '@/hooks/useReviewReplies';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface ReviewReplyDisplayProps {
  reply: ReviewReply;
  canEdit: boolean;
  canDelete: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ReviewReplyDisplay: React.FC<ReviewReplyDisplayProps> = ({
  reply,
  canEdit,
  canDelete,
  onEdit,
  onDelete
}) => {
  const isPending = reply.status === 'Pending Validation';
  const isRejected = reply.status === 'Rejected';

  return (
    <div className={cn(
      "mt-4 p-4 rounded-lg border-l-4",
      isPending && "bg-amber-50 dark:bg-amber-950/20 border-l-amber-400",
      isRejected && "bg-red-50 dark:bg-red-950/20 border-l-red-400",
      !isPending && !isRejected && "bg-blue-50 dark:bg-blue-950/20 border-l-blue-400"
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center flex-wrap gap-2 mb-2 min-w-0">
          <Building2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="font-medium text-blue-600 truncate">{reply.business_name}</span>
          <Badge variant="secondary" className="text-xs">
            Business Reply
          </Badge>
          
          {isPending && (
            <Badge variant="outline" className="flex items-center text-amber-500 border-amber-200 bg-amber-50">
              <Clock className="w-3 h-3 mr-1" />
              <span>Pending</span>
            </Badge>
          )}
          
          {isRejected && (
            <Badge variant="destructive" className="text-xs">
              Rejected
            </Badge>
          )}
        </div>

        {(canEdit || canDelete) && (
          <div className="flex gap-1">
            {canEdit && onEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="h-7 w-7 p-0"
              >
                <Edit className="h-3 w-3" />
              </Button>
            )}
            
            {canDelete && onDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive/90"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Reply</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this reply? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={onDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      <p className="text-sm text-foreground mb-2 break-words overflow-wrap-anywhere">{reply.content}</p>
      
      <div className="text-xs text-muted-foreground">
        {formatDistanceToNow(new Date(reply.created_at), { addSuffix: true })}
      </div>
    </div>
  );
};

export default ReviewReplyDisplay;
